import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase.ts';
import {
  calcMineProduction, calcMineEnergyConsumption,
  calcSolarPlantEnergy, calcFusionPlantEnergy, calcFusionPlantFuelConsumption,
  calcStorage, calcEffectiveProduction,
} from '@nova-sigma/shared';

interface ProductionRates {
  metal: number;
  crystal: number;
  deuterium: number;
  energy_produced: number;
  energy_consumed: number;
}

interface StorageLimits {
  metal: number;
  crystal: number;
  deuterium: number;
}

interface ResourceState {
  metal: number;
  crystal: number;
  deuterium: number;
}

// Singleton-State damit mehrere useResources-Aufrufe dieselben Daten teilen
let globalResources: ResourceState = { metal: 0, crystal: 0, deuterium: 0 };
let globalProduction: ProductionRates = { metal: 0, crystal: 0, deuterium: 0, energy_produced: 0, energy_consumed: 0 };
let globalStorage: StorageLimits = { metal: 100000, crystal: 100000, deuterium: 100000 };
let listeners: Array<() => void> = [];
let watchedPlanetId: string | null = null;

function notifyListeners() {
  listeners.forEach(fn => fn());
}

export function useResources() {
  const [, forceUpdate] = useState(0);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1);
    listeners.push(listener);

    // Planetendaten laden
    loadPlanetData();

    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  }, []);

  // Animation Frame: Ressourcen client-seitig hochzählen
  useEffect(() => {
    function tick(time: number) {
      // On first frame, initialise the reference to avoid a huge first delta
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = time;
      }
      const deltaMs = time - lastTimeRef.current;
      lastTimeRef.current = time;

      const deltaTicks = deltaMs / 300_000; // 5 min in ms
      const prod = globalProduction;

      globalResources = {
        metal:     Math.min(globalResources.metal     + prod.metal     * deltaTicks, globalStorage.metal),
        crystal:   Math.min(globalResources.crystal   + prod.crystal   * deltaTicks, globalStorage.crystal),
        deuterium: Math.min(globalResources.deuterium + prod.deuterium * deltaTicks, globalStorage.deuterium),
      };

      notifyListeners();
      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return {
    resources: globalResources,
    production: globalProduction,
    storage: globalStorage,
  };
}

async function loadPlanetData() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  // Heimatplanet-ID laden
  const { data: planetRow } = await supabase
    .from('planets')
    .select('id')
    .eq('user_id', user.id)
    .eq('is_homeworld', true)
    .maybeSingle();

  const planetId = planetRow?.id;
  if (!planetId) return;

  // Subscribe once to planet + buildings changes so resources refresh automatically
  if (watchedPlanetId !== planetId) {
    watchedPlanetId = planetId;
    supabase
      .channel(`res-watch-${planetId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'planets', filter: `id=eq.${planetId}` }, () => loadPlanetData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'buildings', filter: `planet_id=eq.${planetId}` }, () => loadPlanetData())
      .subscribe();
  }

  // Lazy calc via RPC — nur wenn planetId vorhanden
  const { data: rpcResult } = await supabase
    .rpc('get_planet_resources', { p_id: planetId });

  const planet = rpcResult && rpcResult.length > 0 ? rpcResult[0] : null;
  if (!planet) return;

  // Gebäude laden
  const { data: buildings } = await supabase
    .from('buildings')
    .select('building_type, level')
    .eq('planet_id', planetId);

  // Forschung laden
  const { data: research } = await supabase
    .from('research')
    .select('research_type, level')
    .eq('user_id', user.id);

  if (!buildings || !research) return;

  const bld = Object.fromEntries(buildings.map(b => [b.building_type, b.level]));
  const res = Object.fromEntries(research.map(r => [r.research_type, r.level]));

  const metalLevel     = bld['metal_mine'] ?? 0;
  const crystalLevel   = bld['crystal_mine'] ?? 0;
  const deutLevel      = bld['deuterium_synthesizer'] ?? 0;
  const solarLevel     = bld['solar_plant'] ?? 0;
  const fusionLevel    = bld['fusion_plant'] ?? 0;
  const metalStoreLvl  = bld['metal_storage'] ?? 0;
  const crystalStoreLvl= bld['crystal_storage'] ?? 0;
  const deutStoreLvl   = bld['deuterium_tank'] ?? 0;
  const miningTech     = res['mining_tech'] ?? 0;
  const energyTech     = res['energy_tech'] ?? 0;

  const energyProduced = calcSolarPlantEnergy(solarLevel)
    + calcFusionPlantEnergy(fusionLevel, energyTech);

  const energyConsumed = calcMineEnergyConsumption('metal', metalLevel)
    + calcMineEnergyConsumption('crystal', crystalLevel)
    + calcMineEnergyConsumption('deuterium', deutLevel)
    + calcFusionPlantFuelConsumption(fusionLevel);

  const metalProd   = calcEffectiveProduction(calcMineProduction('metal', metalLevel),     energyProduced, energyConsumed, miningTech);
  const crystalProd = calcEffectiveProduction(calcMineProduction('crystal', crystalLevel), energyProduced, energyConsumed, miningTech);
  const deutProd    = calcEffectiveProduction(calcMineProduction('deuterium', deutLevel),  energyProduced, energyConsumed, miningTech)
    - calcFusionPlantFuelConsumption(fusionLevel);

  globalResources = {
    metal:     planet.metal,
    crystal:   planet.crystal,
    deuterium: planet.deuterium,
  };

  globalProduction = {
    metal:          metalProd,
    crystal:        crystalProd,
    deuterium:      deutProd,
    energy_produced: energyProduced,
    energy_consumed: energyConsumed,
  };

  globalStorage = {
    metal:     calcStorage(metalStoreLvl),
    crystal:   calcStorage(crystalStoreLvl),
    deuterium: calcStorage(deutStoreLvl),
  };

  notifyListeners();
}
